/**
 * @module  public/js/bootstrap/app.js
 * @summary Initializes core services & stores, exposing a frozen DI container.
 *
 * @description
 * Creates singleton instances for Auth, API, CRM, Firestore, analytics, feature-flags, and modals.
 * Bundles these—plus account/contact/deal/task stores—into a read-only DI container.
 *
 * ## Key Features
 * – Spin-up of all core services with injected dependencies.
 * – Construction & exposure of Accounts, Contacts, Deals, and Tasks stores.
 * – DOMContentLoaded hook to wire loading indicators.
 * – Frozen export prevents runtime mutation of shared singletons.
 *
 * ## Public API (DI keys)
 * – auth, errorUI, apiClient, crmService, fsService, analytics, modal, featureFlag
 * – crmConnectionService, loading (spinner registry)
 * – accountsStore, contactsStore, dealsStore, tasksStore
 * – aiReportService, contactMetaService, taskCompletionService, messagingService
 * – cacheUtils, firestoreHelpers
 *
 * ## Dependencies
 * { AccountsStore, ApiClient, AiReportService, AnalyticsService, AuthService,
 *   ContactMetaService, CRMConnectionService, CRMService, endpoints,
 *   ErrorDisplayService, FeatureFlagService, FirestoreService,
 *   LoadingIndicatorService, ModalService, TaskCompletionService, TasksStore }
 *
 * @author   Auto-Generated
 * @version  1.1.0
 * @since    2025-05-02
 */

import RoleGate from '../security/rolegate.js';

import AccountsStore from '../stores/accountsstore.js';
import ContactsStore from '../stores/contactsstore.js';
import DealsStore from '../stores/dealsstore.js';
import TasksStore from '../stores/tasksstore.js';

import AuthService from '../services/authservice.js';
import ApiClient from '../core/apiclient.js';
import CRMService from '../core/crmservice.js';
import FirestoreService from '../core/firestoreservice.js';
import useTenant from '../core/useTenant.js';
import { firebaseApp } from '../core/firebase-service.js';
import FeatureFlagService from '../services/featureflagservice.js';
import CRMConnectionService from '../services/crmconnectionservice.js';
import endpoints from '../core/endpoints-service.js';
import AnalyticsService from '../services/analyticsservice.js';
import ModalService from '../services/modalservice.js';
import EmailService from '../services/emailservice.js';
import TemplateService from '../services/templateservice.js';
import ErrorDisplayService from '../services/errordisplayservice.js';
import LoadingIndicatorService from '../services/loadingindicatorservice.js';
import { applyStoredAppThemePreference } from '../focusmode/preferences.js';
import { createDialer } from '../services/dialers/dialerindex.js';
import { getController } from '../controllers/stepControllerIndex.js';
import { DEFAULT_TWILIO_EDGE } from '../services/twilioEdgeConfiguration.js';
import { withActionableCallableErrors } from '../utils/callableErrorUtils.js';

import {
  getFunctions,
  httpsCallable,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-functions.js';

/* Ensure Firebase Auth is evaluated in this entry chunk.  The import is
 * tree-shakable side-effect-free, but its presence guarantees the browser
 * actually downloads firebase-auth.js before any callables fire. */
import { getAuth } from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-auth.js';

let canCallValue = null;
let twilioReadyValue = null;

/* Anchor the singleton so it can’t be tree-shaken even in prod builds. */
getAuth(firebaseApp);
applyStoredAppThemePreference();

/* ─────── Firebase Functions helper ─────── */
const fns = getFunctions(firebaseApp, 'us-central1');
const SEQUENCE_ELIGIBILITY_CALLABLE_TIMEOUT_MS = 70_000;
const SEQUENCE_ENROLMENT_CLOSE_CALLABLE_TIMEOUT_MS = 130_000;
export const callables = {
  bulkEnrol: httpsCallable(fns, 'bulkEnrolCallable'),
  resetSequenceEligibility: withActionableCallableErrors(
    httpsCallable(fns, 'resetSequenceEligibilityCallable', {
      timeout: SEQUENCE_ELIGIBILITY_CALLABLE_TIMEOUT_MS,
    }),
    {
      action: 'Resetting sequence eligibility',
      timeoutMs: SEQUENCE_ELIGIBILITY_CALLABLE_TIMEOUT_MS,
    }
  ),
  closeSequenceEnrolments: withActionableCallableErrors(
    httpsCallable(fns, 'closeSequenceEnrolmentsCallable', {
      timeout: SEQUENCE_ENROLMENT_CLOSE_CALLABLE_TIMEOUT_MS,
    }),
    {
      action: 'Closing sequence enrolments',
      timeoutMs: SEQUENCE_ENROLMENT_CLOSE_CALLABLE_TIMEOUT_MS,
    }
  ),
  resumeProblemEmailSequence: httpsCallable(
    fns,
    'resumeProblemEmailSequenceCallable'
  ),
  getProblemEmailSequenceSummary: httpsCallable(
    fns,
    'getProblemEmailSequenceSummaryCallable'
  ),
  fillQueue: httpsCallable(fns, 'allocateLeadsCallable'),
  getQueueMixPolicy: httpsCallable(fns, 'getQueueMixPolicy'),
  saveQueueMixPolicy: httpsCallable(fns, 'saveQueueMixPolicy'),
  deferFocusModeTasks: httpsCallable(fns, 'deferFocusModeTasksCallable'),
  updateFocusModeStepTemplates: httpsCallable(
    fns,
    'updateFocusModeStepTemplates'
  ),
  setAllocatorCron: httpsCallable(fns, 'setAllocatorCronCallable'),
  generateAiReports: httpsCallable(fns, 'generateAiReports', {
    timeout: 600_000,
  }),
  leadPipelineApproveFinalQa: httpsCallable(fns, 'leadPipelineApproveFinalQa'),
  leadPipelineGetOperatorOverview: httpsCallable(
    fns,
    'leadPipelineGetOperatorOverview'
  ),
  leadPipelineGetIdentificationPlan: httpsCallable(
    fns,
    'leadPipelineGetIdentificationPlan'
  ),
  leadPipelineSubmitAcquisitionBatch: httpsCallable(
    fns,
    'leadPipelineSubmitAcquisitionBatch'
  ),
  selfServeGetOnboarding: httpsCallable(fns, 'selfServeGetOnboarding'),
  selfServeListSubmissions: httpsCallable(fns, 'selfServeListSubmissions'),
  selfServeCompleteIntegrationSetup: httpsCallable(
    fns,
    'selfServeCompleteIntegrationSetup'
  ),
  selfServeSaveAccountSetup: httpsCallable(fns, 'selfServeSaveAccountSetup'),
  selfServeSavePlaybookDraft: httpsCallable(fns, 'selfServeSavePlaybookDraft'),
  selfServeSubmitOnboarding: httpsCallable(fns, 'selfServeSubmitOnboarding'),
  selfServeReviewOnboarding: httpsCallable(fns, 'selfServeReviewOnboarding'),
  selfServeResetOnboarding: httpsCallable(fns, 'selfServeResetOnboarding'),
  leadPipelineGetAutomationPolicy: httpsCallable(
    fns,
    'leadPipelineGetAutomationPolicy'
  ),
  leadPipelineUpdateAutomationPolicy: httpsCallable(
    fns,
    'leadPipelineUpdateAutomationPolicy'
  ),
  leadPipelineListDemandEvaluations: httpsCallable(
    fns,
    'leadPipelineListDemandEvaluations'
  ),
  leadPipelineCreateDemandQaSnapshot: httpsCallable(
    fns,
    'leadPipelineCreateDemandQaSnapshot'
  ),
  leadPipelineBulkReviewDemand: httpsCallable(
    fns,
    'leadPipelineBulkReviewDemand'
  ),
  leadPipelineGetRun: httpsCallable(fns, 'leadPipelineGetRun'),
  leadPipelineListCandidates: httpsCallable(fns, 'leadPipelineListCandidates'),
  leadPipelineRecordApproval: httpsCallable(fns, 'leadPipelineRecordApproval'),
  leadPipelineRetryStage: httpsCallable(fns, 'leadPipelineRetryStage'),
  leadPipelineResumeRun: httpsCallable(fns, 'leadPipelineResumeRun'),
  leadPipelineGetEnrichmentPolicy: httpsCallable(
    fns,
    'leadPipelineGetEnrichmentPolicy'
  ),
  leadPipelineUpdateEnrichmentPolicy: httpsCallable(
    fns,
    'leadPipelineUpdateEnrichmentPolicy'
  ),
  leadPipelineGetPipelineProfile: httpsCallable(
    fns,
    'leadPipelineGetPipelineProfile'
  ),
  leadPipelineUpdatePipelineProfile: httpsCallable(
    fns,
    'leadPipelineUpdatePipelineProfile'
  ),
  leadPipelinePreviewPipelineProfile: httpsCallable(
    fns,
    'leadPipelinePreviewPipelineProfile'
  ),
  leadPipelineCreatePipelineTestReceiptRun: httpsCallable(
    fns,
    'leadPipelineCreatePipelineTestReceiptRun'
  ),
  leadPipelinePublishPipelineProfile: httpsCallable(
    fns,
    'leadPipelinePublishPipelineProfile'
  ),
  generateEmailBrainstormIdeas: httpsCallable(
    fns,
    'generateEmailBrainstormIdeas',
    {
      timeout: 120_000,
    }
  ),
  rewriteEmailDraft: httpsCallable(fns, 'rewriteEmailDraft', {
    timeout: 120_000,
  }),
  placeGlobalContact: httpsCallable(fns, 'placeGlobalContact'),
  upsertGlobalCompany: httpsCallable(fns, 'upsertGlobalCompany'),
  bulkUpsertGlobalCompanies: httpsCallable(fns, 'bulkUpsertGlobalCompanies'),
  promoteStagingContactsToGlobal: httpsCallable(
    fns,
    'promoteStagingContactsToGlobal'
  ),
  globalContactsRequestRampedupEnrichment: httpsCallable(
    fns,
    'globalContactsRequestRampedupEnrichment'
  ),
  globalContactsRequestFullenrichEnrichment: httpsCallable(
    fns,
    'globalContactsRequestFullenrichEnrichment'
  ),
  globalContactsGetEnrichmentSchedulerSettings: httpsCallable(
    fns,
    'globalContactsGetEnrichmentSchedulerSettings'
  ),
  globalContactsSetEnrichmentSchedulerSettings: httpsCallable(
    fns,
    'globalContactsSetEnrichmentSchedulerSettings'
  ),
  upsertCompanyScoringModel: httpsCallable(fns, 'upsertCompanyScoringModel'),
  computeTenantCompanyScores: httpsCallable(fns, 'computeTenantCompanyScores', {
    timeout: 120_000,
  }),
  stageCompanyForTenant: httpsCallable(fns, 'stageCompanyForTenant'),
  applyGlobalOverlay: httpsCallable(fns, 'applyGlobalOverlay'),
  previewGlobalContactsOverlay: httpsCallable(
    fns,
    'previewGlobalContactsOverlay'
  ),
  applyGlobalContactsOverlay: httpsCallable(fns, 'applyGlobalContactsOverlay'),
  reviewAttachToEmail: httpsCallable(fns, 'globalContactsReviewAttachToEmail'),
  reviewAttachToLinkedIn: httpsCallable(
    fns,
    'globalContactsReviewAttachToLinkedIn'
  ),
  reviewKeepSeparate: httpsCallable(fns, 'globalContactsReviewKeepSeparate'),
  identityLookup: httpsCallable(fns, 'globalContactsIdentityLookup'),
  globalContactsResolveReview: httpsCallable(
    fns,
    'globalContactsResolveReview'
  ),
  globalContactsUpdateQuality: httpsCallable(
    fns,
    'globalContactsUpdateQuality'
  ),
  globalContactsPlacementPreview: httpsCallable(
    fns,
    'globalContactsPlacementPreview'
  ),
  globalContactsDelete: httpsCallable(fns, 'globalContactsDelete'),
  createUserWithReset: httpsCallable(fns, 'createUserWithReset'),
  deleteUserWithArtifacts: httpsCallable(fns, 'deleteUserWithArtifacts'),
  savePlaybookTarget: httpsCallable(fns, 'savePlaybookTarget'),
  publishPlaybookTargetVersion: httpsCallable(
    fns,
    'publishPlaybookTargetVersion'
  ),
  runPlaybookTargetReport: httpsCallable(fns, 'runPlaybookTargetReport', {
    timeout: 120_000,
  }),
  runPlaybookSequenceComparison: httpsCallable(
    fns,
    'runPlaybookSequenceComparison',
    { timeout: 120_000 }
  ),
  // put future callables here ⤴
};

/* ───── new util / service imports ───── */
import AiReportService from '../services/aireportservice.js';
import ContactMetaService from '../services/contactmetaservice.js';
import TaskCompletionService from '../services/taskcompletionservice.js';
import { getCached } from '../utils/cacheUtils.js';
import { docWithTenant, writeWithTenant } from '../utils/firestoreHelpers.js';
import { getCurrentCRM } from '../services/crmUtils.js';
import SequenceEnrollmentService from '../services/sequenceEnrollmentService.js';
import EmailConnectionService from '../services/emailconnectionservice.js';
import TaskReplyService from '../services/taskreplyservice.js';
import ContactsStagingService from '../services/contactsStagingService.js';
import FocusModeTaskService from '../services/focusModeTaskService.js';
import MirrorNotesService from '../services/mirrornotesservice.js';
import MessagingService from '../services/messagingService.js';
import { hydrateAppConfig } from './appConfigLoader.js';
import { startGuidanceClient } from '../guidance/guidanceClient.js';

/* ─────────── core singletons ─────────── */
export const auth = new AuthService();
export const errorUI = new ErrorDisplayService();

export const apiClient = new ApiClient({
  authService: auth,
  errorDisplayService: errorUI,
});
export const crmService = new CRMService({
  endpointService: endpoints,
  crmResolver: getCurrentCRM,
});
export const fsService = new FirestoreService();
fsService.init(firebaseApp);
export const analytics = new AnalyticsService({ authService: auth });
export const modal = new ModalService();
export const emailService = new EmailService({
  firestoreService: fsService,
  authService: auth,
  apiClient,
  endpoints,
});
export const featureFlag = new FeatureFlagService({
  authService: auth,
  firestoreService: fsService,
});
export const templateService = new TemplateService({
  firestoreService: fsService,
});
export const tenant = useTenant();

export const appConfigReady = hydrateAppConfig({
  auth,
});

/* ───── security ───── */
export const rolegate = new RoleGate(auth);

/* ─────────── new singleton services ─────────── */
export const aiReportService = new AiReportService({ apiClient, endpoints });

export const contactMetaService = new ContactMetaService({
  apiClient,
  endpoints,
  auth,
  crmUtils: { getCurrentCRM },
});

export const taskCompletionService = new TaskCompletionService({
  apiClient,
  endpoints,
  crmUtils: { getCurrentCRM },
  analyticsService: analytics,
  auth,
});

export const taskReplyService = new TaskReplyService({
  firestoreService: fsService,
});

export const contactsStagingService = new ContactsStagingService({ auth });

export const messagingService = new MessagingService({
  apiClient,
  authService: auth,
});

export const mirrorNotesService = new MirrorNotesService({
  auth,
  crmUtils: { getCurrentCRM },
});

/* ─────────── Email OAuth helper ─────────── */
export const emailConnectionService = new EmailConnectionService({
  apiClient,
  auth,
  endpoints,
});

/* ─────────── Sequence-engine helper ─────────── */
export const sequenceEnrollmentService = new SequenceEnrollmentService({
  apiClient,
  authService: auth,
  endpoints,
  crmUtils: { getCurrentCRM }, // <-- inject helper
});

/* ─────────── CRM-link helper ─────────── */
export const crmConnectionService = new CRMConnectionService({
  endpointService: endpoints,
  authService: auth,
  apiClient,
});

/* ───────── loading indicator ───────── */
export const loading = new LoadingIndicatorService();
document.addEventListener('DOMContentLoaded', () => {
  const safeRegister = (name, id) => {
    const el = document.getElementById(id);
    if (el) loading.register(name, el);
  };

  safeRegister('tasks', 'tasksSpinner');
  safeRegister('taskSave', 'taskSavingSpinner');
  safeRegister('accounts', 'accountsSpinner');
  safeRegister('kanban', 'kanbanSpinner');
  safeRegister('reports', 'reportsSpinner');
});

/* ─────────── stores ─────────── */
export const accountsStore = new AccountsStore({
  apiClient,
  endpoints,
  authService: auth,
  crmConnectionService,
  firestoreService: fsService,
});

export const contactsStore = new ContactsStore({
  ApiClient: apiClient,
  Endpoints: endpoints,
  AuthService: auth,
  CRMService: crmService,
});

export const dealsStore = new DealsStore({
  apiClient,
  endpoints,
  authService: auth,
  crmConnectionService,
  loadingIndicatorService: loading,
});

export const tasksStore = new TasksStore({
  apiClient,
  endpoints,
  authService: auth,
  crmConnectionService,
  loadingIndicatorService: loading,
});

export const focusModeTaskService = new FocusModeTaskService({
  auth,
  tasksStore, // DEPRECATED: legacy path
  taskCompletionService,
  deferFocusModeTasks: callables.deferFocusModeTasks,
  crmUtils: { getCurrentCRM },
  apiClient,
  endpoints,
});

/* freeze for safety */
// Allow pages to opt-out of loading the Twilio voice dialer by setting
// `window.__DISABLE_DIALER = true` before this bootstrap runs.
const disableDialer =
  typeof window !== 'undefined' && window.__DISABLE_DIALER === true;
const dialerInstance = disableDialer
  ? null
  : createDialer(window?.env?.DIALER_PROVIDER || 'twilio', {
      apiClient,
      endpoints,
    });

const diCore = {
  /* core */
  apiClient,
  rolegate,
  endpoints,
  dialer: dialerInstance,
  /* common services */
  auth,
  crmService,
  fsService,
  featureFlag,
  analytics,
  modal,
  modalService: modal,
  tenant,
  errorUI,
  loading,
  crmConnectionService,
  emailConnectionService,
  templateService,
  /* domain stores */
  accountsStore,
  contactsStore,
  dealsStore,
  tasksStore,
  /* new helpers */
  aiReportService,
  contactMetaService,
  taskCompletionService,
  focusModeTaskService,
  taskReplyService,
  contactsStagingService,
  mirrorNotesService,
  sequenceEnrollmentService,
  emailService,
  messagingService,
  cacheUtils: { getCached },
  firestoreHelpers: { docWithTenant, writeWithTenant },
  crmUtils: { getCurrentCRM },
  stepControllerIndex: { getController },
  callables,
  twilioEdge: DEFAULT_TWILIO_EDGE,
};

Object.defineProperties(diCore, {
  canCall: {
    get: () => canCallValue,
    set: (value) => {
      canCallValue = !!value;
    },
    enumerable: true,
    configurable: false,
  },
  twilioReady: {
    get: () => twilioReadyValue,
    set: (promise) => {
      twilioReadyValue = promise;
    },
    enumerable: true,
    configurable: false,
  },
});

const di = Object.freeze(diCore);

export default di;

if (typeof window !== 'undefined') {
  window.di = di;
  startGuidanceClient(di);
}
